import Tippy, { useSingleton } from "@tippyjs/react";
import React, { createContext, useContext, useMemo } from "react";
import { Placement, TooltipProps } from "./Tooltip";

type SingletonTarget = ReturnType<typeof useSingleton>[1];

interface TooltipGroupContextValue {
  target: SingletonTarget;
}

const TooltipGroupContext = createContext<TooltipGroupContextValue | null>(
  null,
);

/**
 * Hook to access the TooltipGroup context
 * Returns null if not inside a TooltipGroup
 */
export const useTooltipGroup = () => useContext(TooltipGroupContext);

/**
 * Props for the TooltipGroup component
 */
export interface TooltipGroupProps {
  /**
   * Child elements containing Tooltip components
   */
  children: React.ReactNode;

  /**
   * Delay in ms before showing/hiding the tooltip
   * Can be a single number or [show delay, hide delay]
   * @default 400
   */
  delay?: number | [number | null, number | null];

  /**
   * Duration in ms for the show/hide animation
   * Can be a single number or [show duration, hide duration]
   * @default [275, 0]
   */
  duration?: number | [number | null, number | null];

  /**
   * Default placement of tooltips relative to target elements
   * @default "bottom-end"
   */
  placement?: Placement;

  /**
   * Offset of tooltips from target elements
   * Format is [skidding, distance] in pixels
   * @default [0, 0]
   */
  offset?: [number, number];

  /**
   * Visual variant for all tooltips in the group
   * @default "default"
   */
  variant?: "default" | "light" | "dark";

  /**
   * Size variant for all tooltips in the group
   * @default "md"
   */
  size?: "sm" | "md" | "lg";

  /**
   * Props that individual tooltips can override
   * @default ["content", "placement", "offset"]
   */
  overrides?: Array<keyof TooltipProps>;

  /**
   * CSS transition for when the tooltip moves between elements.
   * Helps with smooth repositioning when quickly moving between targets.
   * @default "transform 0.1s ease-out"
   */
  moveTransition?: string;
}

/**
 * TooltipGroup Component
 *
 * A wrapper component that uses Tippy.js's singleton feature to optimize
 * performance when rendering many tooltips together. Instead of creating
 * separate popper instances for each tooltip, all tooltips in a group
 * share a single instance.
 *
 * This is particularly useful for:
 * - Dense grids with tooltips on each cell
 * - Tables with tooltips on row/column headers
 * - Lists with tooltips on each item
 *
 * @example
 * // Basic usage - all tooltips share delay and placement
 * <TooltipGroup delay={300} placement="top">
 *   <Tooltip content="First tooltip">
 *     <button>Button 1</button>
 *   </Tooltip>
 *   <Tooltip content="Second tooltip">
 *     <button>Button 2</button>
 *   </Tooltip>
 * </TooltipGroup>
 *
 * @example
 * // Individual tooltips can override placement and offset
 * <TooltipGroup placement="top">
 *   <Tooltip content="Uses group placement">
 *     <button>Button 1</button>
 *   </Tooltip>
 *   <Tooltip content="Custom placement" placement="bottom">
 *     <button>Button 2</button>
 *   </Tooltip>
 * </TooltipGroup>
 */
export const TooltipGroup: React.FC<TooltipGroupProps> = ({
  children,
  delay = 400,
  duration = [275, 0],
  placement = "bottom-end",
  offset = [0, 0],
  variant = "default",
  size = "md",
  overrides = ["content", "placement", "offset"],
  moveTransition = "transform 0.1s ease-out",
}) => {
  const tooltipClassName = [
    "indigo-tooltip",
    `indigo-tooltip-${variant}`,
    `indigo-tooltip-${size}`,
  ].join(" ");

  const [source, target] = useSingleton({
    overrides: overrides as (
      | "content"
      | "placement"
      | "offset"
      | "delay"
      | "duration"
    )[],
  });

  const contextValue = useMemo(() => ({ target }), [target]);

  return (
    <TooltipGroupContext.Provider value={contextValue}>
      {/* Hidden singleton source - provides shared configuration */}
      <Tippy
        singleton={source}
        delay={delay}
        duration={duration}
        placement={placement}
        offset={offset}
        className={tooltipClassName}
        moveTransition={moveTransition}
      />
      {children}
    </TooltipGroupContext.Provider>
  );
};

export default TooltipGroup;
